import axios, { AxiosResponse } from "axios";
import { getToken } from "./authApi";
import { ApiResponse, BaseResponse, BaseResponseWithData, baseUrl, GetParams, getWithAuth, getWithAuthAndParams, Guid, ListRespone, PaginationListRespone, postWithAuth } from "./baseApi";
import { CampaignPeriod, CampaignStatus } from "./campaingsApi"
import { BaseChannelModel } from "./channelsApi";
import { formatDate } from "../services/dateService";
import { MediaContentType } from "./mediaApi";

export enum ReportAdhocStatus {
    Requested = 0,
    InProgress = 1,
    Generated = 2,
    PartiatlyGenerated = 3
}

export type ChannelAndCampaignReportRow = {
    channel: string,
    campaigns: CampaignReportRow[]
}

export type CampaignReportRow = {
    name: string,
    externalCampaignId: string,
    status: CampaignStatus,
    periods: CampaignPeriod[],
    durationMs: number,
    playouts: number
}

export type MediaReportRow = {
    startDateTime: string,
    endDateTime: string,
    status: ReportAdhocStatus,
    dateRequested: string,
    downloadUrl: string
}

export type SkinReportRow = {
    channelName: string,
    mosaicName: string,
    itemName: string,
    durationMs: number,
    playouts: number
}

export type generateAdhocReportRequest = {
    startDateTime: string,
    endDateTime: string
}

export type generateAdhocReportResponse = {

}

export enum ReportSubscriptionType {
    NetworkAndCampaignYesterday = 0,
    NetworkAndCampaignWeekToDate = 1,
    NetworkAndCampaignLastWeek = 2,
    NetworkAndCampaignLast7Days = 3,
    NetworkAndCampaignLastMonth = 4,
    NetworkAndCampaignMonthToDate = 5,

    AdHocYesterday = 100,
    AdHocWeekToDate = 101,
    AdHocLastWeek = 102,
    AdHocLast7Days = 103,
    AdHocLastMonth = 104,
    AdHocMonthToDate = 105,
}

export type ReportSubscription = {
    reportSubscriptionType: ReportSubscriptionType
}

export type PlayoutsPerDate = {
    date: string,
    playouts: number,
    durationSeconds: number
}

export type PlayoutsReportRow = {
    player: string,
    campaign: string,
    network: string,
    media: string,
    contentType: MediaContentType,
    playouts: number,
    duration: number
}

export type PlayoutsReportResponse = {
    tableRows: PlayoutsReportRow[],
    columns: ReportDashboardColumns[],
    chart: PlayoutsPerDate[],
    totalPlayouts: number,
    totalDurationString: string,
    totalDurationSeconds: number
}

export enum ReportDashboardColumns {
    Network = 0,
    Campaign = 1,
    Player = 2,
    Media = 3,
    ContentType = 4,
}

export type ReportDashboardRequest = {
    startTime: string,
    endTime: string
    columns: ReportDashboardColumns[],
    networkIds: Guid[],
    campaignIds: Guid[],
    playerIds: Guid[],
    mediaIds: Guid[],
    contentTypes: number[],
}


export const getCampaignAndChannelReport = async (campaigns: Guid[], channels: Guid[], statuses: CampaignStatus[], startDate: string, endDate: string, page: number) : Promise<ApiResponse<PaginationListRespone<ChannelAndCampaignReportRow>>> => {
    var params = new GetParams();
    params.Add("startDate", startDate);
    params.Add("endDate", endDate);
    params.Add("page", page+"");
    params.AddList("channelIds", channels);
    params.AddList("campaignsIds", campaigns);
    params.AddList("campaignStatus", statuses.map(it => it.toString()));
    return await getWithAuthAndParams<PaginationListRespone<ChannelAndCampaignReportRow>>(`${baseUrl}reports/channel`, params); 
}

export const getCampaignAndChannelReportDownloadLink = (campaigns: Guid[], channels: Guid[], statuses: CampaignStatus[], startDate: string, endDate: string) : string => {
    var params = new GetParams();
    var token = getToken();
    if(token)    params.Add("token", token);
    params.Add("startDate", startDate);
    params.Add("endDate", endDate);
    params.AddList("channelIds", channels);
    params.AddList("campaignsIds", campaigns);
    params.AddList("campaignStatus", statuses.map(it => it.toString()));
    return `${baseUrl}reports/channel/download${params.query}`
}

export const generateAdhocReport = async(request : generateAdhocReportRequest) : Promise<ApiResponse<generateAdhocReportResponse>> => {
    return await postWithAuth<generateAdhocReportRequest, generateAdhocReportResponse>(`${baseUrl}reports/adhoc`, request);
}

export const getAdhocReports = async () : Promise<ApiResponse<ListRespone<MediaReportRow>>> => {
    return await getWithAuth<ListRespone<MediaReportRow>>({url: `${baseUrl}reports/adhoc`}); 
}

export const getSkinReports = async (skins: Guid[], sites: Guid[], startDate: string, endDate: string):Promise<ApiResponse<ListRespone<SkinReportRow>>> => {
    var params = new GetParams();
    params.Add("startDate", startDate);
    params.Add("endDate", endDate);
    params.AddList("skinsIds", skins);
    params.AddList("siteIds", sites);
    return await getWithAuthAndParams<ListRespone<SkinReportRow>>(`${baseUrl}reports/skin`, params)
}

export const generateSkinAdhocReport = async(request : generateAdhocReportRequest) : Promise<ApiResponse<generateAdhocReportResponse>> => {
    return await postWithAuth<generateAdhocReportRequest, generateAdhocReportResponse>(`${baseUrl}reports/skin-adhoc`, request);
}

export const getSkinAdhocReports = async () : Promise<ApiResponse<ListRespone<MediaReportRow>>> => {
    return await getWithAuth<ListRespone<MediaReportRow>>({url: `${baseUrl}reports/skin-adhoc`}); 
}

export const getReportSubscriptions = async () : Promise<ApiResponse<ListRespone<ReportSubscription>>> => {
    return await getWithAuth<ListRespone<ReportSubscription>>({url: `${baseUrl}reports/subscribtions`}); 
}

export const reportSubscribe = async(request : ReportSubscription) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<ReportSubscription, BaseResponse>(`${baseUrl}reports/subscribtions/subscribe`, request);
}

export const reportUnsubscribe = async(request : ReportSubscription) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<ReportSubscription, BaseResponse>(`${baseUrl}reports/subscribtions/unsubscribe`, request);
}

export const getReportsDashboardData = async(request: ReportDashboardRequest) : Promise<ApiResponse<BaseResponseWithData<PlayoutsReportResponse>>> => {
    return await postWithAuth<ReportDashboardRequest, BaseResponseWithData<PlayoutsReportResponse>>(`${baseUrl}reports/dashboard`, request);
}

export const postReportsDashboardDataRequest = async(request: ReportDashboardRequest) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<ReportDashboardRequest, BaseResponse>(`${baseUrl}reports/adhoc/request`, request);
}

export const downloadCSVReportsDashboardData = async (request: ReportDashboardRequest) : Promise<{data: any, filename: string}> => {
    const response: AxiosResponse<Blob> = await axios.post(`${baseUrl}reports/dashboard/csv`, request, {
        headers: {
          'Authorization' : getToken()
        },
        responseType: 'blob',
    });

    const fileName =  `dashboard_report_${formatDate(new Date().toISOString())}.csv`;

    return {
        data: response.data,
        filename: fileName
    }
}